// Single breadcrumb-style dropdown replacing the two stacked switchers
// that used to live in the sidebar. Click → grouped menu with workspaces
// as headers and that workspace's projects nested beneath. One click to
// switch either dimension.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Project, type Workspace } from "../lib/api";
import Avatar from "./Avatar";
import { usePrompt } from "./PromptDialog";
import { writeActiveProjectId, writeActiveWorkspaceId } from "./Layout";

interface Props {
  workspaces: Workspace[];
  activeWorkspace: Workspace;
  projects: Project[];                  // projects of the active workspace
  activeProject: Project | null;
}

export default function ScopeSwitcher({ workspaces, activeWorkspace, projects, activeProject }: Props) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const prompt = usePrompt();
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const switchWorkspace = (id: string) => {
    writeActiveWorkspaceId(id);
    qc.invalidateQueries({ queryKey: ["workspaces"] });
    setOpen(false);
    nav(`/?w=${id}`);
  };
  const switchProject = (id: string) => {
    writeActiveProjectId(activeWorkspace.id, id);
    setOpen(false);
    const params = new URLSearchParams(window.location.search);
    params.set("prj", id);
    nav({ pathname: window.location.pathname, search: `?${params.toString()}` });
  };

  const newWorkspace = useMutation({
    mutationFn: (name: string) => api.workspaces.create(name),
    onSuccess: (w) => {
      writeActiveWorkspaceId(w.id);
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      setOpen(false);
      nav(`/?w=${w.id}`);
    },
  });
  const newProject = useMutation({
    mutationFn: (name: string) => api.projects.create({ workspace_id: activeWorkspace.id, name }),
    onSuccess: (p) => {
      writeActiveProjectId(activeWorkspace.id, p.id);
      qc.invalidateQueries({ queryKey: ["projects", activeWorkspace.id] });
      qc.invalidateQueries({ queryKey: ["collections", activeWorkspace.id] });
      setOpen(false);
      const params = new URLSearchParams(window.location.search);
      params.set("prj", p.id);
      nav({ pathname: window.location.pathname, search: `?${params.toString()}` });
    },
  });

  return (
    <div className="relative min-w-0" ref={wrap}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-surface-2 max-w-full"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar
          src={activeWorkspace.logo_updated_at ? api.avatars.workspaceUrl(activeWorkspace.id, activeWorkspace.logo_updated_at) : null}
          name={activeWorkspace.name}
          email={activeWorkspace.name + "@workspace"}
          size={24}
          rounded="md"
        />
        <span className="text-sm font-semibold truncate max-w-[140px]" title={activeWorkspace.name}>{activeWorkspace.name}</span>
        {activeProject && (
          <>
            <Chevron />
            <span className="text-sm muted truncate max-w-[140px]" title={activeProject.name}>{activeProject.name}</span>
          </>
        )}
        <CaretDown />
      </button>

      {open && (
        <div role="menu" className="absolute z-50 left-0 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 text-sm w-80 max-h-[70vh] overflow-y-auto scroll-pane">
          {workspaces.map((w) => {
            const isActive = w.id === activeWorkspace.id;
            return (
              <div key={w.id} className="px-1 py-1">
                <div className={"group flex items-center gap-1 rounded " + (isActive ? "" : "hover:bg-surface-2")}>
                  <button
                    type="button"
                    onClick={() => switchWorkspace(w.id)}
                    className={
                      "flex-1 min-w-0 flex items-center gap-2 px-2 py-1.5 rounded text-left " +
                      (isActive ? "text-text" : "text-text-dim hover:text-text")
                    }
                  >
                    <Avatar
                      src={w.logo_updated_at ? api.avatars.workspaceUrl(w.id, w.logo_updated_at) : null}
                      name={w.name}
                      email={w.name + "@workspace"}
                      size={22}
                      rounded="md"
                    />
                    <span className="flex-1 truncate font-semibold">{w.name}</span>
                    {isActive && <span className="chip bg-surface-3 muted-strong text-[10px]">current</span>}
                    {w.role === "owner" && <span className="chip bg-surface-3 muted-strong text-[10px]">owner</span>}
                  </button>
                  {isActive && (
                    <button
                      type="button"
                      onClick={() => { setOpen(false); nav("/settings"); }}
                      title="Workspace settings"
                      aria-label="Workspace settings"
                      className="h-7 w-7 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2 mr-1"
                    >
                      <SettingsIcon />
                    </button>
                  )}
                </div>

                {isActive && (
                  <ul className="pl-9 my-1 space-y-0.5">
                    {projects.map((p) => {
                      const active = p.id === activeProject?.id;
                      return (
                        <li key={p.id}>
                          <button
                            type="button"
                            onClick={() => switchProject(p.id)}
                            className={
                              "w-full flex items-center gap-2 px-2 py-1 rounded text-left text-[13px] " +
                              (active ? "bg-accent-soft text-accent-bright" : "text-text-dim hover:bg-surface-2 hover:text-text")
                            }
                          >
                            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-60" />
                            <span className="flex-1 truncate">{p.name}</span>
                            {!!p.is_default && <span className="chip bg-surface-3 muted-strong text-[10px]">default</span>}
                          </button>
                        </li>
                      );
                    })}
                    <li>
                      <button
                        type="button"
                        onClick={async () => {
                          const name = await prompt({ title: "New project", placeholder: "Project name", confirmLabel: "Create", inputLabel: "Name" });
                          if (name) newProject.mutate(name);
                        }}
                        className="w-full flex items-center gap-2 px-2 py-1 rounded text-left text-[13px] text-text-faint hover:bg-surface-2 hover:text-text"
                      >
                        <span className="text-base leading-none">＋</span>
                        <span>New project</span>
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            );
          })}

          <div className="border-t border-border my-1" />
          <button
            type="button"
            onClick={async () => {
              const name = await prompt({ title: "New workspace", placeholder: "Workspace name", confirmLabel: "Create", inputLabel: "Name" });
              if (name) newWorkspace.mutate(name);
            }}
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded hover:bg-surface-2 text-left text-text-dim hover:text-text"
          >
            <span className="text-base leading-none">＋</span>
            <span>New workspace</span>
          </button>
        </div>
      )}
    </div>
  );
}

function Chevron() {
  return <span className="text-text-faint">›</span>;
}
function SettingsIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function CaretDown() {
  return (
    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" aria-hidden="true" className="shrink-0 text-text-faint">
      <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
